import type { ReactNode } from 'react'
import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import CurrencyContext from 'contexts/currencyContext'
import GeoContext from 'contexts/geoContext'
import ModalContext from 'contexts/ModalContext'
import BodyText from 'LuxKit/Typography/BodyText'
import Flag from 'LuxKit/Flag'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import ContactContext from 'contexts/contactContext'
import LogoIcon from 'components/App/Header/Logo/LogoIcon'
import Clickable from 'components/Common/Clickable'
import NavBarCurrencyLanguageModal from 'components/NavKit/NavBarButtons/NavBarCurrencyLanguageModal'
import { useVisibleLanguageOptions } from 'components/NavKit/NavBarButtons/useVisibleLanguageOptions'

const FooterActionCard = styled(Clickable)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(12)};
  padding: ${rem(8)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.M};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  text-align: start;
  text-decoration: none;
`

const FooterActionContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  min-width: 0;
`

const FooterActionText = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
`

const FooterActionIcon = styled.div`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: ${rem(40)};
  height: ${rem(40)};
`

const FooterChevron = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: ${rem(24)};
  height: ${rem(24)};
`

const FooterQuickLinks = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  gap: ${rem(8)};
  min-height: ${rem(44)};
`

const FooterQuickLink = styled(Clickable)`
  display: flex;
  flex-shrink: 1;
  align-items: center;
  gap: ${rem(6)};
  min-width: 0;
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  text-decoration: none;
`

const FooterQuickLinkButton = styled.button`
  display: flex;
  flex-shrink: 1;
  align-items: center;
  gap: ${rem(6)};
  min-width: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  cursor: pointer;
`

const FooterQuickLinkIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
`

interface Props {
  closeDrawer: () => void
  staffTools?: ReactNode
}

function TripPlannerMobileFooter({
  closeDrawer,
  staffTools,
}: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const supportContact = useContext(ContactContext)
  const currentCurrency = useContext(CurrencyContext)
  const { currentRegionCode } = useContext(GeoContext)
  const { showLanguageSelector } = useVisibleLanguageOptions()

  const handleRegionSelectorOpen = useCallback(() => {
    showModal(
      <NavBarCurrencyLanguageModal
        showLanguageTab={showLanguageSelector}
        tabletHeight="auto"
      />,
    )
  }, [showLanguageSelector, showModal])

  const backToHomepageTitle = intl.formatMessage({
    id: 'du_SWWjEg8DNttWs4sgt',
    defaultMessage: 'Back to homepage',
    description: 'Title for the mobile TripPlanner drawer action that returns the user to the Luxury Escapes homepage',
  })
  const backToHomepageSubtitle = intl.formatMessage({
    id: '96fHKJi_ognbpDeswS5m',
    defaultMessage: 'Return to luxuryescapes.com',
    description: 'Subtitle for the mobile TripPlanner drawer action that returns the user to the Luxury Escapes homepage',
  })
  const callSupportLabel = intl.formatMessage({
    id: 'uE8qxalaKqFkKWGnCxqE',
    defaultMessage: 'Call customer support 24/7 on {phoneNumber}',
    description: 'Accessibility label for the compact mobile TripPlanner drawer link that calls customer support',
  }, { phoneNumber: supportContact.local.humanReadable })
  const regionSelectorLabel = intl.formatMessage({
    id: 'p-nJ-jUqIhGEo3KFBKPs',
    defaultMessage: 'Change currency or language',
    description: 'Accessibility label for the mobile TripPlanner drawer currency and language selector action',
  })

  return (
    <>
      <FooterActionCard to="/" onClick={closeDrawer}>
        <FooterActionContent>
          <FooterActionIcon>
            <LogoIcon height={32} width={32} mobileVariant="icon" tabletVariant="icon" desktopVariant="icon" />
          </FooterActionIcon>
          <FooterActionText>
            <BodyText variant="medium" weight="bold">
              {backToHomepageTitle}
            </BodyText>
            <BodyText variant="small" colour="light-foreground-alt">
              {backToHomepageSubtitle}
            </BodyText>
          </FooterActionText>
        </FooterActionContent>
        <FooterChevron aria-hidden="true">
          <LineAngleRightIcon />
        </FooterChevron>
      </FooterActionCard>
      <FooterQuickLinks>
        <FooterQuickLink
          href={`tel:${supportContact.local.number}`}
          aria-label={callSupportLabel}
          onClick={closeDrawer}
        >
          <FooterQuickLinkIcon>
            <LinePhoneIcon size="S" />
          </FooterQuickLinkIcon>
          <BodyText variant="medium">
            {supportContact.local.humanReadable}
          </BodyText>
        </FooterQuickLink>
        <FooterQuickLinkButton type="button" aria-label={regionSelectorLabel} onClick={handleRegionSelectorOpen}>
          <FooterQuickLinkIcon>
            <Flag countryCode={currentRegionCode} size="S" />
          </FooterQuickLinkIcon>
          <BodyText variant="medium">
            {currentCurrency.toUpperCase()}
          </BodyText>
        </FooterQuickLinkButton>
      </FooterQuickLinks>
      {staffTools}
    </>
  )
}

export default TripPlannerMobileFooter
